/**
 * @fileoverview ContentCard — Foundry's `bp6-card` host used inside settings
 * sub-pages (e.g. the "Passkeys" card on /settings/authentication). Three
 * slots:
 *   - title (h6 + optional muted count) on the left of the title row
 *   - right-side title-row actions (typically a primary button)
 *   - children (the card body, often a list of EntityRow items)
 *
 * Foundry mapping: `control-panel-internal-components__content-card`. The
 * title row sits above a `bp6-divider` which separates it from the body.
 */

import { Card, Divider } from "@blueprintjs/core";
import type { ReactNode } from "react";

interface ContentCardProps {
  title: string;
  /** Optional muted suffix shown next to the title (e.g. "(1/4)"). */
  titleCount?: ReactNode;
  /** Right side of the title row — typically a Button. */
  titleActions?: ReactNode;
  /** Card body — typically a stack of EntityRow children. */
  children: ReactNode;
  className?: string;
}

export function ContentCard({
  title,
  titleCount,
  titleActions,
  children,
  className,
}: ContentCardProps) {
  return (
    <Card data-testid="content-card"
      elevation={0}
      className={["bp-content-card", className].filter(Boolean).join(" ")}
    >
      <div className="bp-content-card-title-row">
        <div className="bp-content-card-title-left">
          <h6 className="bp6-heading bp-content-card-title">
            {title}
            {titleCount !== undefined && (
              <span className="bp6-text-muted bp-content-card-title-count"> {titleCount}</span>
            )}
          </h6>
        </div>
        {titleActions !== undefined && (
          <div className="bp-content-card-title-right">{titleActions}</div>
        )}
      </div>
      <Divider className="bp-content-card-divider" />
      <div className="bp-content-card-body">{children}</div>
    </Card>
  );
}
